/**
 * 커스텀 확인 다이얼로그 — 글래스모피즘 톤, 화면 정중앙.
 *
 * 사용:
 *   const [open, setOpen] = useState(false)
 *   <ConfirmDialog
 *     open={open}
 *     title="채팅을 종료할까요?"
 *     description="이 작업은 되돌릴 수 없습니다."
 *     confirmText="종료"
 *     cancelText="취소"
 *     variant="danger"
 *     onConfirm={() => { ... ; setOpen(false) }}
 *     onCancel={() => setOpen(false)}
 *   />
 */

import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, X } from 'lucide-react'

export type ConfirmVariant = 'danger' | 'warning' | 'info'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: ConfirmVariant
  loading?: boolean
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}

const variantStyles: Record<ConfirmVariant, { iconColor: string; confirmBtn: string; ring: string }> = {
  danger: {
    iconColor: 'text-red-400',
    confirmBtn: 'bg-red-500 hover:bg-red-600 text-white',
    ring: 'ring-red-500/20',
  },
  warning: {
    iconColor: 'text-amber-400',
    confirmBtn: 'bg-amber-500 hover:bg-amber-600 text-white',
    ring: 'ring-amber-500/20',
  },
  info: {
    iconColor: 'text-blue-400',
    confirmBtn: 'bg-blue-500 hover:bg-blue-600 text-white',
    ring: 'ring-blue-500/20',
  },
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmText = '확인',
  cancelText = '취소',
  variant = 'danger',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  // ESC 로 취소
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, loading, onCancel])

  // body scroll lock
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const styles = variantStyles[variant]

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="confirm-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => {
            if (!loading) onCancel()
          }}
        >
          <motion.div
            key="confirm-dialog"
            initial={{ opacity: 0, scale: 0.92, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 8 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className={`glass-card relative w-full max-w-md rounded-2xl border border-white/10 shadow-glass-lg ring-1 ${styles.ring}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
          >
            {/* 닫기 X */}
            <button
              type="button"
              onClick={() => !loading && onCancel()}
              disabled={loading}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-dark-400 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:pointer-events-none"
              aria-label="닫기"
            >
              <X className="w-4 h-4" />
            </button>

            {/* 본문 */}
            <div className="p-6 sm:p-7">
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-11 h-11 rounded-full bg-white/5 flex items-center justify-center ${styles.iconColor}`}>
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0 pr-6">
                  <h3
                    id="confirm-dialog-title"
                    className="text-base sm:text-lg font-semibold text-white leading-snug"
                  >
                    {title}
                  </h3>
                  {description && (
                    <p className="mt-2 text-sm text-dark-300 leading-relaxed whitespace-pre-line">
                      {description}
                    </p>
                  )}
                </div>
              </div>

              {/* 버튼 영역 */}
              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="glass-button px-4 py-2 rounded-lg text-sm text-dark-100 hover:text-white disabled:opacity-50 disabled:pointer-events-none transition"
                >
                  {cancelText}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={loading}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-60 disabled:pointer-events-none ${styles.confirmBtn}`}
                >
                  {loading ? '처리 중…' : confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  // 항상 body 에 portal — 어떤 부모 stacking context 에도 영향 안 받게
  if (typeof document === 'undefined') return null
  return createPortal(content, document.body)
}

export default ConfirmDialog
